import React, { Suspense, useCallback, useEffect } from 'react';
import {
  BrowserRouter as Router, Redirect, Switch, Route, useParams, matchPath,
} from 'react-router-dom';
import { Provider, useDispatch } from 'react-redux';
import { I18nextProvider, useTranslation } from 'react-i18next';
import HyperDX from '@hyperdx/browser';
import { actions, AppStore } from '@/store';
import { EUserPermission } from '@/store/types';
import { pageLocale } from '@/constant/path-user';
import { LangSymbolString } from './constant/types';
import i18nGlobal, { I18nNS } from './i18n';
import TokenManager from './utils/token-manager';
import getUserInfo from './fetches/user/user-profile/get-user-profile';
import useAppDispatch from './hooks/use-app-dispatch';
import Loader from './components/common/loader';
import useSelectorCommon from './hooks/use-selector-common';
import { pageAdminLocale } from './constant/path-admin';
import getKeywords from './fetches/common/get-keywords';
import getCurriculumns from './fetches/common/get-curriculums';
import { updateUserInfo } from './utils/update-user-info';
import getGrades from './fetches/common/get-grades';
import getSubjects from './fetches/common/get-subjects';
import { openSocialPwModal } from './components/common/auth-modal/component/social-password';
import { IS_PROD } from './constant/config';
import SEO from './components/common/SEO';

const RouterUser = React.lazy(() => import('@/components/routes/router-user'));
const RouterAdmin = React.lazy(() => import('@/components/routes/router-admin'));

const App: React.FC = () => {
  const params = useParams<{ locale?: string }>();
  const { profile } = useSelectorCommon('userInfo');
  const { i18n } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    let { locale } = params;
    locale = locale ? locale.toLowerCase() : i18n.language;
    if (locale !== i18n.language) {
      const isLangValid = Object.values(LangSymbolString).some((lang) => locale === lang);
      if (isLangValid) {
        i18n.changeLanguage(locale);
      }
    }
  }, [i18n, params]);

  useEffect(() => {
    document.getElementsByTagName('html')[0]?.setAttribute('lang', i18n.language);
  }, [i18n.language]);

  const checkAndUpdateUserStatus = useCallback(() => {
    const token = TokenManager.getToken();
    const { permission } = AppStore.getState().common.userInfo.profile;
    if (token.at && token.rt && permission === EUserPermission.VISITOR) {
      updateUserInfo();
    } else if (!(token.at && token.rt) && permission !== EUserPermission.VISITOR) {
      dispatch(actions.common.userInfo.initProfile());
    }
  }, [dispatch]);

  useEffect(() => {
    const interval = setInterval(checkAndUpdateUserStatus, 1000);
    return () => clearInterval(interval);
  }, [checkAndUpdateUserStatus]);

  return (
    <Switch>
      {profile.permission >= EUserPermission.ADMIN && (
        <Route path={pageAdminLocale.HOME} component={RouterAdmin} />
      )}
      <Route path={pageLocale.HOME} component={RouterUser} />
    </Switch>
  );
};

const AppWithProfile: React.FC = ({ children }) => {
  const {
    isLoaded, isRequiredPasswordChange, loadMessage, profile,
  } = useSelectorCommon('userInfo');
  const dispatch = useAppDispatch();

  // update userInfo state before dispatch to it
  useEffect(() => {
    (async () => {
      const { at, rt } = TokenManager.getToken();
      try {
        const promises: Promise<any>[] = [];
        if (at && rt) {
          promises.push(
            getUserInfo().then((userInfo) => {
              if (userInfo) {
                dispatch(actions.common.userInfo.setProfile(userInfo));
              }
            }),
          );
        }
        promises.push(
          getKeywords().then(({ data, translation }) => {
            dispatch(actions.common.constants.setKeywords(data));
            Object.entries(translation).forEach(([lang, resources]) => {
              i18nGlobal.addResources(lang, I18nNS.KEYWORDS, resources);
            });
          }),
        );
        promises.push(
          getCurriculumns().then(({ data, translation }) => {
            dispatch(actions.common.constants.setCurriculums(data));
            Object.entries(translation).forEach(([lang, resources]) => {
              i18nGlobal.addResources(lang, I18nNS.CURRICULUMS, resources);
            });
          }),
        );
        promises.push(
          getGrades().then(({ data, translation }) => {
            dispatch(actions.common.constants.setGrades(data));
            Object.entries(translation).forEach(([lang, resources]) => {
              i18nGlobal.addResources(lang, I18nNS.GRADES, resources);
            });
          }),
        );
        promises.push(
          getSubjects().then(({ data, translation }) => {
            dispatch(actions.common.constants.setSubjects(data));
            Object.entries(translation).forEach(([lang, resources]) => {
              i18nGlobal.addResources(lang, I18nNS.SUBJECTS, resources);
            });
          }),
        );
        await Promise.all(promises);
      } catch (e) {
        //
      }
      dispatch(actions.common.userInfo.setIsLoaded(true));
    })();
  }, [dispatch]);

  // set hyper dx session id
  useEffect(() => {
    if (IS_PROD) {
      if (profile && profile.email) {
        HyperDX.setGlobalAttributes({
          userEmail: profile.email,
          userId: profile.uuid,
        });
      }
    }
  }, [profile]);

  if (!isLoaded) {
    return <Loader loadingText={loadMessage} />;
  }

  if (isRequiredPasswordChange) {
    openSocialPwModal();
  }

  return <>{children}</>;
};

const LocaleRouter: React.FC = () => (
  <Router>
    <Switch>
      <Route path="/:locale" component={App} />
      <Redirect to={`/${i18nGlobal.language}`} />
    </Switch>
  </Router>
);

const vh = window.innerHeight * 0.01;
const resizeVH = () => {
  setTimeout(() => {
    const eVh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--customVh', `${eVh}px`);
  }, 100);
};
const AppWithProvider: React.FC = () => {
  useEffect(() => {
    document.documentElement.style.setProperty('--customVh', `${vh}px`);
    window.addEventListener('resize', resizeVH);
    return () => {
      window.removeEventListener('resize', resizeVH);
    };
  }, []);

  return (
    <Suspense fallback={<Loader />}>
      <I18nextProvider i18n={i18nGlobal}>
        <Provider store={AppStore}>
          <AppWithProfile>
            <SEO />
            <LocaleRouter />
          </AppWithProfile>
        </Provider>
      </I18nextProvider>
    </Suspense>
  );
};

export default AppWithProvider;
